import {
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "../../../firebase";
import {
  BATCH_WRITE_SIZE,
  COL_EXPENSE_IMPORT_MONTHS,
  EXPENSE_CHUNK_CATEGORY,
  EXPENSE_CHUNK_MAX_ROWS,
  EXPENSE_CHUNK_TARGET_BYTES,
  EXPENSE_IMPORT_LINE_CHUNKS_SUB,
  EXPENSE_WEEKS_PER_MONTH,
  expenseWeekCategory,
  isExpenseImportCategory,
} from "../constants";
import { rowMatchesDateRange } from "../../salesDataImport/utils/dateParse";
import { buildMonthWeekMeta, salesImportMonthDocId } from "../../salesDataImport/utils/weekUtils";
import { formatFirebaseError } from "../../salesDataImport/utils/firebaseError";
import { rowTranDateInMonth } from "./expenseExcelParser";

function chunkRowsByApproxBytes(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const chunks = [];
  let current = [];
  let currentBytes = 0;
  for (const row of rows) {
    const rowBytes = JSON.stringify(row).length;
    const wouldExceedBytes = current.length > 0 && currentBytes + rowBytes > EXPENSE_CHUNK_TARGET_BYTES;
    const wouldExceedRows = current.length >= EXPENSE_CHUNK_MAX_ROWS;
    if (wouldExceedBytes || wouldExceedRows) {
      chunks.push(current);
      current = [row];
      currentBytes = rowBytes;
    } else {
      current.push(row);
      currentBytes += rowBytes;
    }
  }
  if (current.length) chunks.push(current);
  return chunks;
}

function chunkDocId(category, index) {
  return `${category}_${index}`;
}

function rowToPayload(row, uploadedBy, uploadedAt, audit) {
  return {
    branchId: row.branchId || "",
    voucherTypeName: row.voucherTypeName || "",
    voucherNo: row.voucherNo || "",
    tranDate: row.tranDate || "",
    partyName: row.partyName || "",
    transactionType: row.transactionType || "",
    amount: Number(row.amount) || 0,
    uploadedAt,
    uploadedBy: uploadedBy || "",
    fileName: audit.fileName || "",
    importBatchId: audit.importBatchId || "",
    weekOfMonth: audit.weekOfMonth ?? null,
  };
}

function flattenCategoryBuckets(buckets) {
  return Object.keys(buckets)
    .map(Number)
    .sort((a, b) => a - b)
    .flatMap((k) => buckets[k] || []);
}

async function listChunkRefsForCategory(monthId, category) {
  const colRef = collection(db, COL_EXPENSE_IMPORT_MONTHS, monthId, EXPENSE_IMPORT_LINE_CHUNKS_SUB);
  const snap = await getDocs(colRef);
  return snap.docs.filter((d) => (d.data()?.category || "") === category).map((d) => d.ref);
}

async function listAllExpenseChunkRefs(monthId) {
  const colRef = collection(db, COL_EXPENSE_IMPORT_MONTHS, monthId, EXPENSE_IMPORT_LINE_CHUNKS_SUB);
  const snap = await getDocs(colRef);
  return snap.docs.filter((d) => isExpenseImportCategory(d.data()?.category)).map((d) => d.ref);
}

async function deleteChunkRefs(refs) {
  for (let i = 0; i < refs.length; i += BATCH_WRITE_SIZE) {
    const batch = writeBatch(db);
    for (const ref of refs.slice(i, i + BATCH_WRITE_SIZE)) {
      batch.delete(ref);
    }
    await batch.commit();
  }
}

function normalizeMonthlyLog(data) {
  if (!data) return null;
  if (data.monthlyLog && typeof data.monthlyLog === "object") return data.monthlyLog;
  if (data.rowCount != null) {
    return {
      rowCount: data.rowCount,
      uploadedAt: data.lastUploadedAt || "",
      uploadedBy: data.lastUploadedBy || "",
      fileName: data.lastFileName || "",
    };
  }
  return null;
}

function normalizeWeeklyLogs(data) {
  const weeklyLogs = {};
  if (!data) return weeklyLogs;
  for (let w = 1; w <= EXPENSE_WEEKS_PER_MONTH; w += 1) {
    const log = data[`weeklyLog_${w}`];
    if (log && (log.rowCount > 0 || log.uploadedAt)) weeklyLogs[w] = log;
  }
  return weeklyLogs;
}

/**
 * @param {"monthly"|"weekly"} uploadMode
 * @param {number|null} weekOfMonth 1–5 when uploadMode is weekly
 */
export async function saveExpenseImportChunks({
  year,
  monthIndex,
  rows,
  uploadedBy,
  fileName = "",
  importBatchId = "",
  onProgress,
  uploadMode = "monthly",
  weekOfMonth = null,
}) {
  const monthId = salesImportMonthDocId(year, monthIndex);
  const uploadedAt = new Date().toISOString();
  const isWeekly = uploadMode === "weekly";

  let category = EXPENSE_CHUNK_CATEGORY;
  let weekMeta = null;
  let filtered;

  if (isWeekly) {
    const w = Number(weekOfMonth);
    if (!w || w < 1 || w > EXPENSE_WEEKS_PER_MONTH) {
      throw new Error("Select week 1–5 for weekly upload.");
    }
    weekMeta = buildMonthWeekMeta(year, monthIndex, w);
    category = expenseWeekCategory(w);
    filtered = (rows || []).filter(
      (r) =>
        r.valid !== false &&
        rowMatchesDateRange(r, weekMeta.fromDate, weekMeta.toDate)
    );
    if (!filtered.length) {
      throw new Error(
        `No valid expense rows for ${weekMeta.label}. Check Tran Date in the file.`
      );
    }
  } else {
    filtered = (rows || []).filter(
      (r) => r.valid !== false && rowTranDateInMonth(r, year, monthIndex)
    );
    if (!filtered.length) {
      throw new Error("No valid expense rows for the selected month.");
    }
  }

  const oldRefs = isWeekly
    ? await listChunkRefsForCategory(monthId, category)
    : await listAllExpenseChunkRefs(monthId);

  const payloads = filtered.map((r) =>
    rowToPayload(r, uploadedBy, uploadedAt, {
      fileName,
      importBatchId,
      weekOfMonth: isWeekly ? Number(weekOfMonth) : null,
    })
  );
  const chunks = chunkRowsByApproxBytes(payloads);

  const monthPatch = {
    year: Number(year),
    monthIndex: Number(monthIndex),
    monthId,
    updatedAt: serverTimestamp(),
    schemaVersion: 2,
  };

  if (isWeekly) {
    monthPatch[`weeklyLog_${weekOfMonth}`] = {
      uploadedAt,
      uploadedBy: uploadedBy || "",
      fileName: fileName || "",
      rowCount: payloads.length,
      fromDate: weekMeta.fromDate,
      toDate: weekMeta.toDate,
      importBatchId: importBatchId || "",
      weekOfMonth: Number(weekOfMonth),
    };
  } else {
    monthPatch.monthlyLog = {
      uploadedAt,
      uploadedBy: uploadedBy || "",
      fileName: fileName || "",
      rowCount: payloads.length,
      importBatchId: importBatchId || "",
    };
    monthPatch.lastUploadedAt = uploadedAt;
    monthPatch.lastUploadedBy = uploadedBy || "";
    monthPatch.lastFileName = fileName || "";
    monthPatch.rowCount = payloads.length;
    for (let w = 1; w <= EXPENSE_WEEKS_PER_MONTH; w += 1) {
      monthPatch[`weeklyLog_${w}`] = deleteField();
    }
  }

  const monthRef = doc(db, COL_EXPENSE_IMPORT_MONTHS, monthId);
  await setDoc(monthRef, monthPatch, { merge: true });

  const newRefs = [];
  for (let i = 0; i < chunks.length; i += 1) {
    if (onProgress) onProgress(`Saving chunk ${i + 1} / ${chunks.length}…`);
    const ref = doc(
      db,
      COL_EXPENSE_IMPORT_MONTHS,
      monthId,
      EXPENSE_IMPORT_LINE_CHUNKS_SUB,
      chunkDocId(category, i)
    );
    await setDoc(ref, {
      category,
      chunkIndex: i,
      rows: chunks[i],
      rowCount: chunks[i].length,
      uploadedAt,
      uploadedBy: uploadedBy || "",
      weekOfMonth: isWeekly ? Number(weekOfMonth) : null,
      fromDate: weekMeta?.fromDate || "",
      toDate: weekMeta?.toDate || "",
    });
    newRefs.push(ref);
  }

  if (oldRefs.length) {
    const toDelete = oldRefs.filter((ref) => !newRefs.some((nr) => nr.path === ref.path));
    if (toDelete.length) await deleteChunkRefs(toDelete);
  }

  return {
    monthId,
    rowCount: payloads.length,
    chunkCount: chunks.length,
    uploadMode,
    weekOfMonth: isWeekly ? Number(weekOfMonth) : null,
    weekLabel: weekMeta?.label || "",
  };
}

export async function loadExpenseImportMeta(year, monthIndex) {
  const monthId = salesImportMonthDocId(year, monthIndex);
  const snap = await getDoc(doc(db, COL_EXPENSE_IMPORT_MONTHS, monthId));
  if (!snap.exists()) return null;
  const data = snap.data() || {};
  const monthlyLog = normalizeMonthlyLog(data);
  const weeklyLogs = normalizeWeeklyLogs(data);
  const weeklyRowTotal = Object.values(weeklyLogs).reduce(
    (s, log) => s + (Number(log?.rowCount) || 0),
    0
  );
  return {
    id: snap.id,
    ...data,
    monthlyLog,
    weeklyLogs,
    hasMonthlyData: Boolean(monthlyLog?.rowCount),
    hasWeeklyData: weeklyRowTotal > 0,
    weeklyRowTotal,
  };
}

export async function loadExpenseImportRows(year, monthIndex) {
  const monthId = salesImportMonthDocId(year, monthIndex);
  const colRef = collection(db, COL_EXPENSE_IMPORT_MONTHS, monthId, EXPENSE_IMPORT_LINE_CHUNKS_SUB);
  const snap = await getDocs(colRef);
  const bucketsByCategory = {};

  snap.forEach((cd) => {
    const data = cd.data() || {};
    const cat = data.category || "";
    if (!isExpenseImportCategory(cat)) return;
    const idx = Number(data.chunkIndex) || 0;
    if (!bucketsByCategory[cat]) bucketsByCategory[cat] = {};
    bucketsByCategory[cat][idx] = data.rows || [];
  });

  const monthlyBuckets = bucketsByCategory[EXPENSE_CHUNK_CATEGORY];
  if (monthlyBuckets) {
    const monthlyRows = flattenCategoryBuckets(monthlyBuckets);
    if (monthlyRows.length) return monthlyRows;
  }

  const merged = [];
  for (let w = 1; w <= EXPENSE_WEEKS_PER_MONTH; w += 1) {
    const cat = expenseWeekCategory(w);
    const buckets = bucketsByCategory[cat];
    if (buckets) merged.push(...flattenCategoryBuckets(buckets));
  }
  return merged;
}

export function formatExpenseUploadError(err) {
  return formatFirebaseError(err);
}
